import React, { useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

/**
 * Admin-only bulk delete with a typed confirmation and a clear preview of
 * what will be removed vs skipped (records still in use are never deleted).
 */
export function BulkDeleteDialog({ open, onOpenChange, moduleLabel, preview, loading, onConfirm }) {
  const [typed, setTyped] = useState('');
  const deletable = preview?.deletable || [];
  const blocked = preview?.blocked || [];
  const ready = typed.trim() === 'DELETE' && deletable.length > 0 && !loading;

  const close = (value) => {
    if (!value) setTyped('');
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" /> Delete {deletable.length} {moduleLabel}
          </DialogTitle>
          <DialogDescription>
            These records move to <strong>Recently deleted</strong> and stay restorable by an admin.
            Nothing is erased from history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[45vh] overflow-y-auto">
          <div>
            <p className="mb-2 text-sm font-semibold">Will be deleted ({deletable.length})</p>
            {deletable.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing in this selection can be deleted.</p>
            ) : (
              <div className="flex flex-wrap gap-1" data-testid="bulk-delete-deletable">
                {deletable.map((row) => (
                  <Badge key={row.id} variant="outline" className="font-mono">{row.reference}</Badge>
                ))}
              </div>
            )}
          </div>

          {blocked.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-semibold text-amber-700">
                Skipped — still in use ({blocked.length})
              </p>
              <div className="space-y-1" data-testid="bulk-delete-blocked">
                {blocked.map((row) => (
                  <div key={row.id} className="flex items-center justify-between gap-3 border px-3 py-1.5 text-sm">
                    <span className="font-mono">{row.reference}</span>
                    <span className="text-muted-foreground">{row.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-sm">Type <span className="font-mono font-bold">DELETE</span> to confirm.</p>
          <Input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            placeholder="DELETE"
            data-testid="bulk-delete-confirm-input"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="bulk-delete-cancel">Cancel</Button>
          <Button
            variant="destructive"
            disabled={!ready}
            onClick={() => onConfirm(typed.trim())}
            data-testid="bulk-delete-confirm"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete {deletable.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkDeleteDialog;
